import type { TextChain } from '@/api/types/stowed-element'
import type { ChainLinkError } from '../types/chain-link-error'
import type { LinkableFrame } from '../types/linkable-frame'
import { chainContaining, hasIncomingLink, hasOutgoingLink } from './chain-queries'

/**
 * Validates whether `source` can link to `target`. Non-text links are impossible by construction
 * upstream (only frames that `collectLinkableFrames` projected are ever offered as ports) —
 * `not-text` is included here purely as a defensive guard.
 * `cross-spread` is a real, explicit check: linking the two pages within one
 * spread is allowed (that restriction was deliberately removed — see history), but chain identity
 * itself is scoped per spread, so two different spreads' frames must never merge into one chain.
 * The check is a no-op whenever either side lacks a `spreadId` (legacy/unstamped frame) — callers
 * must stamp `spreadId` at every `TextBoxData` construction site for this guard to be effective.
 *
 * **`frozen` is deliberately not a rejection, on either side (WMPCM-6972).** Freeze and lock are
 * different states here: `locked` rejects every edit, `frozen` rejects only placement geometry
 * (see `PLACEMENT_GEOMETRY_KEYS` / `shouldBlockShapeUpdate` in `lib/groups/`). Linking neither
 * moves nor resizes a frame — it writes `uid` and `paragraphs` — so a frozen frame is a legal link
 * source *and* a legal link target, and the link must not unfreeze it. Do not add a `source-frozen`
 * / `target-frozen` branch below: the AC requires frozen frames to link, and
 * `text-chain-mutator.test.ts` asserts it. (Note the contrast with photo replacement, which *does*
 * exclude frozen text boxes — see `photo-placement-canvas-bridge.tsx`. That is a different
 * operation, not a precedent for this one.)
 *
 * **`parentGroupId` is a rejection on both sides (WMPCM-7036).** This is the mirror of
 * `isLinkedTextFrame` (`lib/groups/linked-text-group-policy.ts`), which keeps a linked
 * frame out of a group for the reasons given there. Guarding only that direction left the invariant
 * one-sided: a frame could be grouped first and linked afterwards, and the combination is what the
 * delete path cannot survive. `buildDeleteGestureScope` (`center-canvas/hooks/use-clipboard-commands.ts`) announces the
 * *selection*, not the group descendants `deleteSelectedShape` expands to nor the text boxes
 * `deleteTextBoxesUnderDeletedShapesForShapeManager` cascades to, so a chain member removed purely
 * as a cascade victim never reaches `coDeletedIds`. The other members' repairs then treat it as a
 * live survivor — absorbing merged text, anchoring a reflow and having a chain pointer persisted at
 * it — moments before it is removed. Rejecting the link makes that state unreachable rather than
 * enumerating every cascade in the delete scope.
 */
export function canLink(
  chains: TextChain[],
  source: LinkableFrame,
  target: LinkableFrame
): ChainLinkError | null {
  if (source.uid === target.uid) return 'same-frame'
  if (!source.isTextFrame || !target.isTextFrame) return 'not-text'
  if (source.locked) return 'source-locked'
  if (target.locked) return 'target-locked'
  if (source.parentGroupId != null) return 'source-grouped'
  if (target.parentGroupId != null) return 'target-grouped'
  if (source.spreadId != null && target.spreadId != null && source.spreadId !== target.spreadId) {
    return 'cross-spread'
  }

  if (hasOutgoingLink(chains, source.uid)) return 'source-has-next'
  if (hasIncomingLink(chains, target.uid)) return 'target-has-prev'

  const sourceChain = chainContaining(chains, source.uid)
  const targetChain = chainContaining(chains, target.uid)

  if (sourceChain && targetChain && sourceChain.id === targetChain.id) return 'circular'

  return null
}
